import { Injectable } from '@nestjs/common';
import { InjectModel } from '@nestjs/mongoose';
import { Model } from 'mongoose';
import { CustomerStatistics, CustomerStatisticsDocument } from '../schemas/customer-statistics.schema';
import { Types } from 'mongoose';

@Injectable()
export class CustomerStatisticsService {
  constructor(
    @InjectModel(CustomerStatistics.name)
    private customerStatisticsModel: Model<CustomerStatisticsDocument>,
  ) {}

  /**
   * Update or create customer statistics when an order is created
   */
  async updateCustomerStatistics(orderData: {
    phone: string;
    fullName?: string;
    total: number;
    state?: string | Types.ObjectId;
    country?: string | Types.ObjectId;
    city?: string;
    address?: string;
  }): Promise<CustomerStatisticsDocument> {
    const { phone, fullName, total, state, country, city, address } = orderData;

    // Convert string IDs to ObjectIds if needed
    const stateId = state ? (typeof state === 'string' ? new Types.ObjectId(state) : state) : undefined;
    const countryId = country ? (typeof country === 'string' ? new Types.ObjectId(country) : country) : undefined;

    // Find existing customer statistics or create new one
    const existingStats = await this.customerStatisticsModel.findOne({ phone });

    if (existingStats) {
      // Update existing statistics
      existingStats.orderCount += 1;
      existingStats.totalAmount += total;
      existingStats.lastOrderDate = new Date();
      
      // Update location if provided
      if (fullName) existingStats.fullName = fullName;
      if (stateId) existingStats.state = stateId;
      if (countryId) existingStats.country = countryId;
      if (city) existingStats.city = city;
      if (address) existingStats.address = address;

      return await existingStats.save();
    } else {
      // Create new customer statistics
      const newStats = new this.customerStatisticsModel({
        phone,
        fullName: fullName || '',
        orderCount: 1,
        totalAmount: total,
        state: stateId,
        country: countryId,
        city: city || '',
        address: address || '',
        firstOrderDate: new Date(),
        lastOrderDate: new Date(),
      });

      return await newStats.save();
    }
  }

  /**
   * Get top customers by order count
   */
  async getTopCustomersByOrders(limit: number = 10): Promise<CustomerStatisticsDocument[]> {
    return this.customerStatisticsModel
      .find()
      .populate('state', 'name')
      .populate('country', 'name')
      .sort({ orderCount: -1 })
      .limit(limit)
      .exec();
  }

  /**
   * Get top customers by total amount
   */
  async getTopCustomersByAmount(limit: number = 10): Promise<CustomerStatisticsDocument[]> {
    return this.customerStatisticsModel
      .find()
      .populate('state', 'name')
      .populate('country', 'name')
      .sort({ totalAmount: -1 })
      .limit(limit)
      .exec();
  }

  /**
   * Get customer statistics by phone
   */
  async getCustomerByPhone(phone: string): Promise<CustomerStatisticsDocument | null> {
    return this.customerStatisticsModel
      .findOne({ phone })
      .populate('state', 'name')
      .populate('country', 'name')
      .exec();
  }

  /**
   * Get total number of customers
   */
  async getTotalCustomers(): Promise<number> {
    return this.customerStatisticsModel.countDocuments().exec();
  }

  /**
   * Get customers by state
   */
  async getCustomersByState(stateId: string, limit: number = 10): Promise<CustomerStatisticsDocument[]> {
    return this.customerStatisticsModel
      .find({ state: new Types.ObjectId(stateId) })
      .populate('state', 'name')
      .populate('country', 'name')
      .sort({ totalAmount: -1 })
      .limit(limit)
      .exec();
  }

  /**
   * Delete all customer statistics
   */
  async deleteAll(): Promise<void> {
    await this.customerStatisticsModel.deleteMany({}).exec();
  }

  /**
   * Create or update customer statistics (used for recalculation)
   */
  async createOrUpdateCustomer(customerData: {
    phone: string;
    fullName?: string;
    orderCount: number;
    totalAmount: number;
    state?: Types.ObjectId;
    country?: Types.ObjectId;
    city?: string;
    address?: string;
    firstOrderDate?: Date;
    lastOrderDate?: Date;
  }): Promise<CustomerStatisticsDocument> {
    const existing = await this.customerStatisticsModel.findOne({ phone: customerData.phone });

    if (existing) {
      existing.orderCount = customerData.orderCount;
      existing.totalAmount = customerData.totalAmount;
      existing.fullName = customerData.fullName || existing.fullName;
      existing.state = customerData.state || existing.state;
      existing.country = customerData.country || existing.country;
      existing.city = customerData.city || existing.city;
      existing.address = customerData.address || existing.address;
      existing.firstOrderDate = customerData.firstOrderDate || existing.firstOrderDate;
      existing.lastOrderDate = customerData.lastOrderDate || existing.lastOrderDate;
      return await existing.save();
    } else {
      const newStats = new this.customerStatisticsModel(customerData);
      return await newStats.save();
    }
  }
}

